// Global Master Auction — API client + helpers.
// Public endpoints (leaderboard, submit) need no auth. Admin endpoints require
// the X-Admin-Email header (Master Admin email).

const BACKEND: string = (() => {
  const w = (typeof window !== 'undefined'
    ? (window as unknown as Record<string, unknown>).__BACKEND_URL__
    : undefined) as string | undefined;
  return w || ((import.meta.env.VITE_BACKEND_URL as string) ?? '');
})();

export interface LeaderboardRow {
  country: string;
  high_bid: number;
  total_bidders: number;
}
export interface LeaderboardResponse {
  countries: LeaderboardRow[];
  total_bidders: number;
  total_countries: number;
}
export interface BidEntry {
  id: string;
  name: string;
  company: string;
  rcdd_id: string;
  email: string;
  country: string;
  bid_amount: number;
  created_at: string;
}
export interface BidInput {
  name: string;
  company?: string;
  rcdd_id?: string;
  email: string;
  country: string;
  bid_amount: number;
}

async function jsonOrThrow(r: Response): Promise<unknown> {
  if (!r.ok) {
    const e = (await r.json().catch(() => ({}))) as { detail?: string };
    throw new Error(e.detail || `Request failed (${r.status})`);
  }
  return r.json();
}

export async function fetchLeaderboard(): Promise<LeaderboardResponse> {
  return (await jsonOrThrow(await fetch(`${BACKEND}/api/auction/leaderboard`))) as LeaderboardResponse;
}

export interface ActivityEvent {
  country: string;
  bid_amount: number;
  created_at: string;
}

export async function fetchActivity(limit = 8): Promise<ActivityEvent[]> {
  const d = (await jsonOrThrow(await fetch(`${BACKEND}/api/auction/activity?limit=${limit}`))) as { events: ActivityEvent[] };
  return d.events ?? [];
}

export async function submitBid(input: BidInput): Promise<{ ok: boolean; id: string }> {
  const r = await fetch(`${BACKEND}/api/auction/bids`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return (await jsonOrThrow(r)) as { ok: boolean; id: string };
}

function adminHeaders(email: string): Record<string, string> {
  return { 'Content-Type': 'application/json', 'X-Admin-Email': email };
}

export async function adminListBids(email: string): Promise<BidEntry[]> {
  return (await jsonOrThrow(
    await fetch(`${BACKEND}/api/admin/auction/bids`, { headers: adminHeaders(email) }),
  )) as BidEntry[];
}

export async function adminUpdateBid(email: string, id: string, patch: Partial<BidInput>): Promise<void> {
  await jsonOrThrow(
    await fetch(`${BACKEND}/api/admin/auction/bids/${id}`, {
      method: 'PUT',
      headers: adminHeaders(email),
      body: JSON.stringify(patch),
    }),
  );
}

export async function adminDeleteBid(email: string, id: string): Promise<void> {
  await jsonOrThrow(
    await fetch(`${BACKEND}/api/admin/auction/bids/${id}`, {
      method: 'DELETE',
      headers: adminHeaders(email),
    }),
  );
}

export async function adminGetVisibility(email: string): Promise<{ hidden_countries: string[]; all_countries: string[] }> {
  return (await jsonOrThrow(
    await fetch(`${BACKEND}/api/admin/auction/visibility`, { headers: adminHeaders(email) }),
  )) as { hidden_countries: string[]; all_countries: string[] };
}

export async function adminSetVisibility(email: string, hidden_countries: string[]): Promise<void> {
  await jsonOrThrow(
    await fetch(`${BACKEND}/api/admin/auction/visibility`, {
      method: 'POST',
      headers: adminHeaders(email),
      body: JSON.stringify({ hidden_countries }),
    }),
  );
}

export function formatUSD(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

const FLAGS: Record<string, string> = {
  canada: '🇨🇦', 'united states': '🇺🇸', usa: '🇺🇸', us: '🇺🇸', 'u.s.': '🇺🇸',
  'united kingdom': '🇬🇧', uk: '🇬🇧', 'great britain': '🇬🇧', england: '🇬🇧',
  france: '🇫🇷', germany: '🇩🇪', spain: '🇪🇸', italy: '🇮🇹', netherlands: '🇳🇱',
  ireland: '🇮🇪', portugal: '🇵🇹', sweden: '🇸🇪', norway: '🇳🇴', switzerland: '🇨🇭',
  singapore: '🇸🇬', australia: '🇦🇺', 'new zealand': '🇳🇿', japan: '🇯🇵',
  china: '🇨🇳', 'south korea': '🇰🇷', korea: '🇰🇷', india: '🇮🇳', philippines: '🇵🇭',
  indonesia: '🇮🇩', malaysia: '🇲🇾', thailand: '🇹🇭', vietnam: '🇻🇳',
  uae: '🇦🇪', 'united arab emirates': '🇦🇪', 'saudi arabia': '🇸🇦', qatar: '🇶🇦',
  kuwait: '🇰🇼', bahrain: '🇧🇭', oman: '🇴🇲', israel: '🇮🇱', turkey: '🇹🇷',
  brazil: '🇧🇷', mexico: '🇲🇽', argentina: '🇦🇷', chile: '🇨🇱', colombia: '🇨🇴',
  'south africa': '🇿🇦', nigeria: '🇳🇬', egypt: '🇪🇬', kenya: '🇰🇪',
};

export function countryFlag(country: string): string {
  return FLAGS[country.toLowerCase().trim()] ?? '🌐';
}

// Auction close date — October 20 (this year, or next year if already past).
export function auctionCloseDate(): Date {
  const now = new Date();
  let year = now.getFullYear();
  const close = new Date(Date.UTC(year, 9, 20, 23, 59, 59)); // month 9 = October
  if (close.getTime() < now.getTime()) {
    year += 1;
  }
  return new Date(Date.UTC(year, 9, 20, 23, 59, 59));
}

export function daysUntilClose(): number {
  const ms = auctionCloseDate().getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}
